import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Users, TrendingUp, DollarSign, CheckCircle, Clock, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function BrandPortal() {
  const navigate = useNavigate();

  const { data: brandData, isLoading } = useQuery({
    queryKey: ["brand-data"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: membership, error: membershipError } = await supabase
        .from("brand_users" as any)
        .select("*, brands(*)")
        .eq("user_id", user.id)
        .single();

      if (membershipError) throw membershipError;

      const { data: campaigns } = await supabase
        .from("campaigns" as any)
        .select("*")
        .eq("organization_id", (membership as any).brand_id);

      const { data: deals } = await supabase
        .from("deals" as any)
        .select("*");

      return {
        brand: (membership as any).brands,
        role: (membership as any).role,
        campaigns: campaigns || [],
        deals: deals || [],
      };
    },
  });

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!brandData) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-4">
              <h3 className="font-semibold text-lg">No Brand Access</h3>
              <p className="text-muted-foreground">
                You don't have access to any brand account.
              </p>
              <Button onClick={() => navigate("/brand/register")}>
                Register Your Brand
              </Button>
            </div>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  const activeDeals = brandData.deals.filter((d: any) => d.status === "active").length;
  const pendingDeals = brandData.deals.filter((d: any) => d.status === "pending_compliance").length;
  const totalSpend = brandData.deals.reduce((sum: number, d: any) => sum + (d.amount || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{brandData.brand.name}</h1>
              <Badge variant={brandData.brand.compliance_status === "verified" ? "default" : "secondary"}>
                {brandData.brand.compliance_status}
              </Badge>
            </div>
            <p className="text-muted-foreground mt-1">
              {brandData.brand.industry}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate("/brand/marketplace")}
              variant="outline"
            >
              <Users className="mr-2 h-4 w-4" />
              Browse Athletes
            </Button>
            <Button onClick={() => navigate("/brand/campaigns/create")}>
              <Plus className="mr-2 h-4 w-4" />
              New Campaign
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Campaigns</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{brandData.campaigns.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeDeals}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Approval</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingDeals}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Investment</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${totalSpend.toLocaleString()}</div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Campaigns */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Campaigns</CardTitle>
                <CardDescription>Manage your marketing campaigns</CardDescription>
              </div>
              <Button onClick={() => navigate("/brand/campaigns")}>
                View All
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {brandData.campaigns.length > 0 ? (
              <div className="space-y-4">
                {brandData.campaigns.slice(0, 3).map((campaign: any) => (
                  <Card key={campaign.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold">{campaign.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {campaign.description}
                          </p>
                        </div>
                        <Badge>{campaign.status}</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No campaigns yet. Create your first campaign to get started.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
